<template>
  <section class="maintenance-plan-view">
    <!-- MODE A: Create Plan -->
    <div v-if="!isEditing">
      <b-card
        no-body
        class="mb-2"
      >
        <div class="m-2">
          <h4 class="mb-25 font-weight-bolder">
            {{ $t('Create Preventive Maintenance Plan') }}
          </h4>
          <p class="text-muted small mb-0">
            {{ $t('Defina a identificação principal e configure as etapas da manutenção') }}
          </p>
        </div>
      </b-card>

      <b-card no-body>
        <div class="p-2">
          <validation-observer ref="simpleRules">
            <b-form @submit.prevent="validationForm">
              <!-- Section 1: General Info Card -->
              <div class="card bg-transparent border shadow-none mb-2">
                <div class="card-header pb-1 pt-1 d-flex align-items-center">
                  <div class="avatar avatar-light-primary mr-75 p-50">
                    <feather-icon
                      icon="InfoIcon"
                      size="18"
                    />
                  </div>
                  <div>
                    <h6 class="mb-25 font-weight-bolder">
                      {{ $t('Informações Gerais') }}
                    </h6>
                    <small class="text-muted">{{ $t('Defina a identificação principal e detalhes do plano') }}</small>
                  </div>
                </div>
                <div class="card-body pt-0 pb-1">
                  <b-row>
                    <b-col
                      cols="12"
                      md="6"
                    >
                      <b-form-group
                        :label="$t('Name') + '*'"
                        label-for="create-plan-name"
                      >
                        <validation-provider
                          #default="{ errors }"
                          name="name"
                          rules="required"
                        >
                          <b-input-group class="input-group-merge">
                            <b-input-group-prepend is-text>
                              <feather-icon
                                icon="BookmarkIcon"
                                size="15"
                              />
                            </b-input-group-prepend>
                            <b-form-input
                              id="create-plan-name"
                              v-model="maintenanceData.name"
                              :state="errors.length > 0 ? false : null"
                              :placeholder="$t('Nome do plano')"
                            />
                          </b-input-group>
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>

                    <b-col
                      cols="12"
                      md="6"
                    >
                      <b-form-group
                        :label="$t('Note')"
                        label-for="create-plan-note"
                      >
                        <b-input-group class="input-group-merge">
                          <b-input-group-prepend is-text>
                            <feather-icon
                              icon="FileTextIcon"
                              size="15"
                            />
                          </b-input-group-prepend>
                          <b-form-input
                            id="create-plan-note"
                            v-model="maintenanceData.note"
                            :placeholder="$t('Observação opcional')"
                          />
                        </b-input-group>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>
              </div>

              <!-- Section 2: Steps Editor -->
              <div class="mb-2">
                <plan-steps-editor
                  :steps="maintenanceData.preventiveMaintenances"
                  :query="stepQuery"
                  :is-editing="false"
                  @add="addStep"
                  @duplicate="duplicateLastStep"
                  @remove="confirmRemoveStep"
                  @open-import="openImportModal"
                  @update:query="stepQuery = $event"
                />
              </div>

              <!-- Section 3: Bottom Action Bar -->
              <div class="d-flex align-items-center pt-1 border-top mt-2">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  type="submit"
                  variant="primary"
                  class="mr-1"
                  :disabled="isSaving || isLoading"
                >
                  <b-spinner
                    v-if="isSaving"
                    small
                    class="mr-50"
                  />
                  <feather-icon
                    v-else
                    icon="CheckIcon"
                    size="15"
                    class="mr-50"
                  />
                  {{ $t('Salvar e Continuar') }}
                </b-button>
                <b-button
                  v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                  type="button"
                  variant="outline-secondary"
                  @click="goBack"
                >
                  {{ $t('Cancel') }}
                </b-button>
              </div>
            </b-form>
          </validation-observer>
        </div>
      </b-card>
    </div>

    <!-- MODE B: Edit Plan -->
    <div v-else>
      <!-- Header Fixo Card -->
      <b-card
        no-body
        class="mb-2"
      >
        <div class="m-2">
          <h3 class="mb-0 d-flex align-items-center flex-wrap">
            <span>{{ maintenanceData.name || $t('Maintenance Plan') }}</span>
            <b-badge
              variant="light-primary"
              class="ml-1"
            >
              <feather-icon
                icon="LayersIcon"
                size="12"
                class="mr-25"
              />
              {{ stepsCount || 0 }} {{ $t('etapas') }}
            </b-badge>
            <b-badge
              variant="light-info"
              class="ml-50"
            >
              <feather-icon
                icon="TruckIcon"
                size="12"
                class="mr-25"
              />
              {{ linkedAssetsCount || 0 }} {{ $t('ativos') }}
            </b-badge>
          </h3>
          <p
            v-if="maintenanceData.note"
            class="text-muted mt-50 mb-0"
          >
            {{ maintenanceData.note }}
          </p>
        </div>
      </b-card>

      <!-- Abas Card -->
      <b-card no-body>
        <b-tabs
          v-model="tabIndex"
          pills
          class="nav-pill-secondary m-1"
        >
          <!-- Aba 1: Geral e Etapas -->
          <b-tab>
            <template #title>
              <feather-icon
                icon="FileTextIcon"
                size="16"
                class="mr-50"
              />
              <span class="font-weight-bold">{{ $t('General and Steps') }}</span>
              <b-badge
                variant="light-primary"
                pill
                class="ml-50 font-small-2"
              >
                {{ stepsCount || 0 }}
              </b-badge>
            </template>

            <div class="mt-2">
              <validation-observer ref="simpleRules">
                <b-form @submit.prevent="validationForm">
                  <!-- Section 1: Informações Gerais -->
                  <div class="card bg-transparent border shadow-none mb-2">
                    <div class="card-header pb-1 pt-1 d-flex align-items-center">
                      <div class="avatar avatar-light-primary mr-75 p-50">
                        <feather-icon
                          icon="InfoIcon"
                          size="18"
                        />
                      </div>
                      <div>
                        <h6 class="mb-25 font-weight-bolder">
                          {{ $t('Informações Gerais') }}
                        </h6>
                        <small class="text-muted">{{ $t('Defina a identificação principal e detalhes do plano') }}</small>
                      </div>
                    </div>
                    <div class="card-body pt-0 pb-1">
                      <b-row>
                        <b-col
                          cols="12"
                          md="6"
                        >
                          <b-form-group
                            :label="$t('Name') + '*'"
                            label-for="edit-plan-name"
                          >
                            <validation-provider
                              #default="{ errors }"
                              name="name"
                              rules="required"
                            >
                              <b-input-group class="input-group-merge">
                                <b-input-group-prepend is-text>
                                  <feather-icon
                                    icon="BookmarkIcon"
                                    size="15"
                                  />
                                </b-input-group-prepend>
                                <b-form-input
                                  id="edit-plan-name"
                                  v-model="maintenanceData.name"
                                  :state="errors.length > 0 ? false : null"
                                  :placeholder="$t('Nome do plano')"
                                />
                              </b-input-group>
                              <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                          </b-form-group>
                        </b-col>

                        <b-col
                          cols="12"
                          md="6"
                        >
                          <b-form-group
                            :label="$t('Note')"
                            label-for="edit-plan-note"
                          >
                            <b-input-group class="input-group-merge">
                              <b-input-group-prepend is-text>
                                <feather-icon
                                  icon="FileTextIcon"
                                  size="15"
                                />
                              </b-input-group-prepend>
                              <b-form-input
                                id="edit-plan-note"
                                v-model="maintenanceData.note"
                                :placeholder="$t('Observação opcional')"
                              />
                            </b-input-group>
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </div>
                  </div>

                  <!-- Section 2: Etapas do Plano -->
                  <div class="mb-2">
                    <plan-steps-editor
                      :steps="maintenanceData.preventiveMaintenances"
                      :query="stepQuery"
                      :is-editing="true"
                      @add="addStep"
                      @duplicate="duplicateLastStep"
                      @remove="confirmRemoveStep"
                      @open-import="openImportModal"
                      @update:query="stepQuery = $event"
                    />
                  </div>

                  <!-- Section 3: Bottom Action Bar -->
                  <div class="d-flex align-items-center pt-1 border-top mt-2">
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      type="submit"
                      variant="primary"
                      class="mr-1"
                      :disabled="isSaving"
                    >
                      <b-spinner
                        v-if="isSaving"
                        small
                        class="mr-50"
                      />
                      <feather-icon
                        v-else
                        icon="SaveIcon"
                        size="15"
                        class="mr-50"
                      />
                      {{ $t('Salvar alterações') }}
                    </b-button>
                    <b-button
                      v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                      type="button"
                      variant="outline-secondary"
                      @click="goBack"
                    >
                      {{ $t('Voltar') }}
                    </b-button>
                  </div>
                </b-form>
              </validation-observer>
            </div>
          </b-tab>

          <!-- Aba 2: Ativos Vinculados -->
          <b-tab>
            <template #title>
              <feather-icon
                icon="TruckIcon"
                size="16"
                class="mr-50"
              />
              <span class="font-weight-bold">{{ $t('Linked Assets') }}</span>
              <b-badge
                variant="light-info"
                pill
                class="ml-50 font-small-2"
              >
                {{ linkedAssetsCount || 0 }}
              </b-badge>
            </template>

            <div class="mt-2">
              <plan-linked-assets-panel
                :plan-id="maintenanceData.id"
                @update:count="linkedAssetsCount = $event"
              />
            </div>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>

    <!-- Modal Import Steps via Spreadsheet -->
    <plan-steps-import-modal
      ref="importModalRef"
      :plan-id="maintenanceData.id"
      @imported="onStepsImported"
    />
  </section>
</template>

<script>
import { onMounted, ref } from '@vue/composition-api'
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BRow,
  BSpinner,
  BTab,
  BTabs,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import Ripple from 'vue-ripple-directive'
import PlanStepsEditor from './components/PlanStepsEditor.vue'
import PlanStepsImportModal from './components/PlanStepsImportModal.vue'
import PlanLinkedAssetsPanel from './components/PlanLinkedAssetsPanel.vue'
// eslint-disable-next-line import/no-cycle
import useMaintenancePlanForm from './useMaintenancePlanForm'

export default {
  name: 'RegisterMaintenancePlan',
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BRow,
    BSpinner,
    BTab,
    BTabs,
    ValidationObserver,
    ValidationProvider,
    PlanStepsEditor,
    PlanStepsImportModal,
    PlanLinkedAssetsPanel,
  },
  directives: {
    Ripple,
  },
  setup(props, { root }) {
    const form = useMaintenancePlanForm()
    const importModalRef = ref(null)
    const tabIndex = ref(0)
    const linkedAssetsCount = ref(0)

    onMounted(() => {
      const qTab = root.$route ? root.$route.query.tab : null
      if (qTab === 'steps' || qTab === 'etapas') {
        tabIndex.value = 0
      } else if (qTab === 'assets' || qTab === 'ativos') {
        tabIndex.value = 1
      }
    })

    const openImportModal = () => {
      if (importModalRef.value) {
        importModalRef.value.show()
      }
    }

    const onStepsImported = () => {
      if (form.fetchMaintenancePlan) {
        form.fetchMaintenancePlan()
      }
    }

    return {
      ...form,
      importModalRef,
      tabIndex,
      linkedAssetsCount,
      openImportModal,
      onStepsImported,
      required,
    }
  },
}
</script>
